import React, { useState, useEffect } from 'react';
import { generateShareImages, generateDownloadImage } from '../utils/shareImageGenerator';
import { PERSONALITY_CARD_STYLES, mapPersonalityToCard } from '../utils/tarotMapper';

/**
 * ShareImagePreview - Test component to preview generated share AND download images
 *
 * Access at: http://localhost:3000/share-preview
 *
 * Features:
 * - Dropdown to select any of the 8 personality types
 * - Preview all three image formats (Mobile, Desktop, Download)
 * - Download buttons for each format
 */
const ShareImagePreview = () => {
  const personalityTypes = Object.keys(PERSONALITY_CARD_STYLES);
  const [selectedPersonality, setSelectedPersonality] = useState(personalityTypes[0]);
  const [mobileImageUrl, setMobileImageUrl] = useState(null);
  const [desktopImageUrl, setDesktopImageUrl] = useState(null);
  const [downloadImageUrl, setDownloadImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generationTime, setGenerationTime] = useState(0);

  // Generate card data for selected personality
  const getCardData = (personality) => {
    return mapPersonalityToCard(
      personality,
      // Full summary - used for results page and download image
      `This is a test summary for ${personality}. Your quiz answers reveal someone who brings unique energy to every interaction. You have a distinctive way of connecting with others and processing the world around you. Whether you're navigating challenges or celebrating wins, you do it with your own special flair. Keep embracing what makes you, you!`,
      // Short summary - used ONLY for mobile share image
      `You're a ${personality}! Your unique energy lights up every room. Embrace what makes you special.`
    );
  };

  useEffect(() => {
    const generateImages = async () => {
      setLoading(true);
      setError(null);

      // Revoke old URLs
      if (mobileImageUrl) URL.revokeObjectURL(mobileImageUrl);
      if (desktopImageUrl) URL.revokeObjectURL(desktopImageUrl);
      if (downloadImageUrl) URL.revokeObjectURL(downloadImageUrl);

      const startTime = performance.now();
      const cardData = getCardData(selectedPersonality);

      try {
        // Generate both share images AND download image in parallel
        const [shareResult, downloadBlob] = await Promise.all([
          generateShareImages(cardData),
          generateDownloadImage(cardData)
        ]);

        const endTime = performance.now();
        setGenerationTime(Math.round(endTime - startTime));

        // Convert Blobs to URLs for preview
        const mobileUrl = URL.createObjectURL(shareResult.mobileBlob);
        const desktopUrl = URL.createObjectURL(shareResult.desktopBlob);
        const downloadUrl = URL.createObjectURL(downloadBlob);

        setMobileImageUrl(mobileUrl);
        setDesktopImageUrl(desktopUrl);
        setDownloadImageUrl(downloadUrl);
        setLoading(false);
      } catch (err) {
        console.error('Generation error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    generateImages();

    // Cleanup URLs on unmount
    return () => {
      if (mobileImageUrl) URL.revokeObjectURL(mobileImageUrl);
      if (desktopImageUrl) URL.revokeObjectURL(desktopImageUrl);
      if (downloadImageUrl) URL.revokeObjectURL(downloadImageUrl);
    };
  }, [selectedPersonality]);

  const handleDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const cardData = getCardData(selectedPersonality);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Share Image Preview</h1>

      {/* Personality Selector */}
      <div style={styles.selectorContainer}>
        <label style={styles.label}>Select Personality Type:</label>
        <select
          value={selectedPersonality}
          onChange={(e) => setSelectedPersonality(e.target.value)}
          style={styles.select}
        >
          {personalityTypes.map((type) => (
            <option key={type} value={type}>
              {PERSONALITY_CARD_STYLES[type].cardNumber} - {type}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={styles.loadingContainer}>
          <p style={styles.subtitle}>🎨 Generating images for <strong>{selectedPersonality}</strong>...</p>
        </div>
      ) : error ? (
        <div style={styles.errorContainer}>
          <p style={styles.error}>❌ {error}</p>
        </div>
      ) : (
        <>
          <p style={styles.subtitle}>
            Generated in <strong>{generationTime}ms</strong> for <strong>{selectedPersonality}</strong>
          </p>

          <div style={styles.grid}>
            {/* Mobile Preview */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>📱 Mobile (9:16 Story)</h2>
              <p style={styles.dimensions}>1080 x 1920px</p>
              {mobileImageUrl && (
                <>
                  <div style={styles.imageContainer}>
                    <img
                      src={mobileImageUrl}
                      alt="Mobile share preview"
                      style={styles.mobileImage}
                    />
                  </div>
                  <button
                    onClick={() => handleDownload(mobileImageUrl, `jaimee-${selectedPersonality.toLowerCase().replace(/\s+/g, '-')}-mobile.png`)}
                    style={styles.button}
                  >
                    Download Mobile Image
                  </button>
                </>
              )}
              <div style={styles.infoBox}>
                <strong>Best for:</strong>
                <ul style={styles.list}>
                  <li>Instagram Stories</li>
                  <li>TikTok</li>
                  <li>Snapchat</li>
                  <li>WhatsApp Status</li>
                </ul>
              </div>
            </div>

            {/* Desktop Preview */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>💻 Desktop (1:1 Square)</h2>
              <p style={styles.dimensions}>1200 x 1200px</p>
              {desktopImageUrl && (
                <>
                  <div style={styles.imageContainer}>
                    <img
                      src={desktopImageUrl}
                      alt="Desktop share preview"
                      style={styles.desktopImage}
                    />
                  </div>
                  <button
                    onClick={() => handleDownload(desktopImageUrl, `jaimee-${selectedPersonality.toLowerCase().replace(/\s+/g, '-')}-desktop.png`)}
                    style={styles.button}
                  >
                    Download Desktop Image
                  </button>
                </>
              )}
              <div style={styles.infoBox}>
                <strong>Best for:</strong>
                <ul style={styles.list}>
                  <li>Twitter</li>
                  <li>Facebook</li>
                  <li>LinkedIn</li>
                  <li>Pinterest</li>
                </ul>
              </div>
            </div>

            {/* Download Preview - Full Summary Version */}
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>📥 Download (Full Summary)</h2>
              <p style={styles.dimensions}>1080 x ~2200px (dynamic)</p>
              {downloadImageUrl && (
                <>
                  <div style={styles.imageContainer}>
                    <img
                      src={downloadImageUrl}
                      alt="Download image preview"
                      style={styles.mobileImage}
                    />
                  </div>
                  <button
                    onClick={() => handleDownload(downloadImageUrl, `jaimee-${selectedPersonality.toLowerCase().replace(/\s+/g, '-')}-download.jpg`)}
                    style={styles.button}
                  >
                    Download Full Image
                  </button>
                </>
              )}
              <div style={styles.infoBox}>
                <strong>Best for:</strong>
                <ul style={styles.list}>
                  <li>Saving full results</li>
                  <li>Personal keepsake</li>
                  <li>Dark background with full summary</li>
                </ul>
              </div>
            </div>
          </div>

          <div style={styles.captionBox}>
            <h3 style={styles.captionTitle}>📝 Share Caption</h3>
            <pre style={styles.caption}>
              I just discovered I'm a {cardData.cardTitle}! 🃏{'\n\n'}
              What's your personality type? Take the quiz at https://quiz.jaimee.ai
            </pre>
          </div>
        </>
      )}

      <div style={styles.footer}>
        <a href="/" style={styles.link}>← Back to Quiz</a>
        <span style={styles.separator}>|</span>
        <a href="/results-preview" style={styles.link}>Results Preview →</a>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: 'Manrope, sans-serif',
    backgroundColor: '#F5F1E8',
    minHeight: '100vh',
  },
  title: {
    fontSize: '36px',
    fontFamily: 'Fraunces, serif',
    color: '#2C2C2C',
    textAlign: 'center',
    marginBottom: '20px',
  },
  selectorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  label: {
    color: '#2C2C2C',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '16px',
    fontWeight: '600',
  },
  select: {
    padding: '10px 15px',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    borderRadius: '8px',
    border: '2px solid #2C2C2C',
    backgroundColor: 'white',
    color: '#2C2C2C',
    cursor: 'pointer',
    minWidth: '280px',
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    textAlign: 'center',
    marginBottom: '40px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  error: {
    color: '#E63946',
    fontSize: '16px',
    textAlign: 'center',
    padding: '20px',
    backgroundColor: 'rgba(230, 57, 70, 0.1)',
    borderRadius: '8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '40px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2C2C2C',
    marginBottom: '5px',
  },
  dimensions: {
    fontSize: '14px',
    color: '#999',
    marginBottom: '20px',
  },
  imageContainer: {
    backgroundColor: '#F5F1E8',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileImage: {
    maxWidth: '250px',
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  desktopImage: {
    maxWidth: '350px',
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  button: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#FAA21B',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '20px',
    transition: 'all 0.2s ease',
  },
  infoBox: {
    backgroundColor: '#F5F1E8',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#666',
  },
  list: {
    margin: '10px 0 0 0',
    paddingLeft: '20px',
  },
  captionBox: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    marginBottom: '40px',
  },
  captionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2C2C2C',
    marginBottom: '15px',
  },
  caption: {
    backgroundColor: '#F5F1E8',
    padding: '20px',
    borderRadius: '8px',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#2C2C2C',
    fontFamily: 'Manrope, sans-serif',
    whiteSpace: 'pre-wrap',
  },
  footer: {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
  },
  link: {
    color: '#FAA21B',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: '600',
  },
  separator: {
    color: '#999',
  },
};

export default ShareImagePreview;
